import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useQuery, gql } from "@apollo/client";
import Checkbox from "./Checkbox";
import Accordion from "./Accordion";
import { SEARCH_SIZE_TYPE_GROUPS } from "../../../../constants/constants";
import "./SizeFilter.css";

const GET_SEARCH_SIZE_FILTER = gql`
  query GetSearchSizeFilter {
    getSearchSizeFilter {
      size_type_id
      size_type_name
      size_values {
        id
        name
        sort_order
      }
    }
  }
`;

/**
 * 사이즈 필터 (검색 필터용)
 * 카테고리 선택과 무관하게 고정된 4개 그룹(상의·아우터/하의/신발/블레이저)을 노출한다.
 * RN 앱의 CATEGORY_BY_SIZE와 동일하게 size_type_id 기반으로 매핑한다.
 */
const SizeFilter = ({ sizeValueIds, onChange }) => {
  const { data, loading } = useQuery(GET_SEARCH_SIZE_FILTER);

  const groups = useMemo(() => {
    if (!data || !data.getSearchSizeFilter) return [];
    const byId = new Map();
    data.getSearchSizeFilter.forEach((g) => {
      if (!g.size_values || g.size_values.length === 0) return;
      byId.set(Number(g.size_type_id), g);
    });
    return SEARCH_SIZE_TYPE_GROUPS.map(({ id, label }) => {
      const g = byId.get(id);
      if (!g) return null;
      return {
        ...g,
        label,
        size_values: [...g.size_values].sort(
          (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
        ),
      };
    }).filter(Boolean);
  }, [data]);

  const normalizedSelected = useMemo(
    () => (sizeValueIds || []).map((id) => String(id)),
    [sizeValueIds],
  );

  if (loading) {
    return (
      <div className="size-filter">
        <p className="size-filter-loading">로딩 중...</p>
      </div>
    );
  }

  if (groups.length === 0) {
    return null;
  }

  const handleToggle = (id) => {
    const idStr = String(id);
    const isSelected = normalizedSelected.includes(idStr);
    const next = isSelected
      ? normalizedSelected.filter((v) => v !== idStr)
      : [...normalizedSelected, idStr];
    onChange(next);
  };

  return (
    <div className="size-filter">
      {groups.map((group) => {
        return (
          <Accordion
            key={group.size_type_id}
            title={group.label}
            initialOpen={false}
            type="sub"
          >
            <div className="size-filter-options">
              {group.size_values.map((size) => {
                const idStr = String(size.id);
                return (
                  <Checkbox
                    id={`size-${group.size_type_id}-${size.id}`}
                    key={`${group.size_type_id}-${size.id}`}
                    text={size.name}
                    checked={normalizedSelected.includes(idStr)}
                    onChange={() => handleToggle(size.id)}
                  />
                );
              })}
            </div>
          </Accordion>
        );
      })}
    </div>
  );
};

SizeFilter.propTypes = {
  sizeValueIds: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

export default SizeFilter;
